/**
 * @file look-card.ts
 * @description Подсказки подложки секции (`look.card`, phase19.16, партия
 * SB120; замечание владельца 2026-10-19 «меняю тень и скругление подложки —
 * ничего не меняется», план пользователя п. 1).
 *
 * Цвет подложки на витрине (`SectionV2`): фон блока — роль или свой цвет —
 * рисуется на самой подложке; при прозрачном фоне подложка — роль
 * «Карточки» (`sectionSurfaceBg`). Под подложкой всегда фон страницы
 * (у секции с подложкой своего фона нет). Если цвета почти совпадают
 * (контраст ниже `CARD_MERGE_CONTRAST`), края подложки видно только по
 * тени — кабинет подсказывает, где сменить цвет. Фото на подложке и рамка
 * карточек сайта края показывают — подсказки нет.
 *
 * Осознанное ограничение: сравнение — только с цветом «Фон страницы» темы.
 * Картинка фона страницы и рисунки задника (`backdrop`), свечение (`haze`)
 * и свой фон отдельной страницы (`page-background`) не учитываются — их
 * в форме секции нет, а под картинкой контраст всё равно не посчитать.
 */

import {
  contrastRatio,
  requireHexColor,
  resolveSurface,
  toHexColor,
  type SectionLook,
  type SiteTheme,
} from "@chaiberi/shared";

import { opaqueBg } from "../site-fields/color-contrast";
import { sectionBgSample, sectionSurfaceBg } from "./text-style";

/** Порог «почти сливается»: #FFFFFF на #FDFBF7 ≈ 1,04, заметная разница — от 1,15. */
export const CARD_MERGE_CONTRAST = 1.15;

/** Подложка почти сливается с фоном страницы. */
export interface CardMerge {
  /** Цвет подложки, `#FFFFFF` (заглавными, как в кабинете). */
  card: string;
  /** Фон страницы под подложкой. */
  page: string;
  /** Откуда цвет подложки: роль «Карточки» темы или фон самого блока. */
  source: "cards" | "section";
  /** Есть ли у подложки тень (своя или «как у сайта»). */
  shadow: boolean;
}

/** Hex непрозрачным и заглавными; `null` — цвет битый. */
function flatHex(color: string, theme: SiteTheme): string | null {
  return opaqueBg(color, theme.colors)?.toUpperCase() ?? null;
}

/** Слияние подложки с фоном страницы; `null` — подложки нет или её края видны. */
export function cardMergeOf(look: SectionLook, theme: SiteTheme): CardMerge | null {
  if (!look.card.on || theme.shapes.cards.border.width > 0) return null;
  const sample = sectionBgSample(sectionSurfaceBg(look), theme.colors);
  if (sample === "photo") return null;
  const card = flatHex(sample, theme);
  if (!card) return null;
  let page: string;
  let ratio: number;
  try {
    page = toHexColor(resolveSurface(theme.colors, "pageBg")).toUpperCase();
    ratio = contrastRatio(requireHexColor(card), requireHexColor(page));
  } catch {
    // Битый цвет черновика — сравнивать не с чем.
    return null;
  }
  if (ratio >= CARD_MERGE_CONTRAST) return null;
  const shadow = look.card.shadow ?? theme.shapes.cards.shadow;
  return {
    card,
    page,
    source: look.bg.kind === "transparent" ? "cards" : "section",
    shadow: shadow > 0,
  };
}
